/**
 * Settings > Git Sources - connect external Git repositories as GitSources.
 */

import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, Eye, EyeOff, GitBranch, Plus, Trash2 } from 'lucide-react'
import { fetchWithAuth } from '@/lib/api'
import { testIds } from '@/lib/test-ids'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome'
import { useWorkspace } from '@/providers/workspace-provider'

interface GitSourceRow {
  id: string
  fnPathPrefix: string
  repoUrl: string
  branch: string
  subpath: string
  workingBranchPattern: string
  status: string
  lastPulledSha: string | null
  lastPulledAt: string | null
  usesStoredCredentials: boolean
}

function formatDistanceToNow(date: Date | string): string {
  const now = new Date()
  const then = typeof date === 'string' ? new Date(date) : date
  const diffMs = now.getTime() - then.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  if (diffSecs < 60) return 'just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
  return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
}

const statusBadgeClass: Record<string, string> = {
  active: 'bg-green-500/10 text-green-600 border-green-500/20',
  cloning: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20',
  error: 'bg-red-500/10 text-red-600 border-red-500/20',
  paused: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  inactive: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
}

interface AdminOrg {
  id: string
  name: string
  slug: string
  role: string
  color?: string | null
  icon?: string | null
}

function abbreviate(name: string) {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 3)
}

function OrgRadioSelector({
  orgs,
  selectedId,
  onSelect,
}: {
  orgs: AdminOrg[]
  selectedId: string
  onSelect: (id: string) => void
}) {
  const selected = orgs.find((o) => o.id === selectedId)
  if (orgs.length <= 1) return null

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground font-medium">Workspace</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex h-7 items-center gap-1.5 rounded-full border px-2 transition-all',
              'text-muted-foreground hover:text-foreground border-border bg-accent/30',
            )}
            aria-label="Select workspace"
          >
            {/* pip bars */}
            <span className="flex items-center gap-[3px]" aria-hidden>
              {orgs.map((o) => {
                const on = o.id === selectedId
                return (
                  <span
                    key={o.id}
                    className={cn('block h-[14px] w-[3px] rounded-full transition-all duration-150', on ? 'opacity-100' : 'opacity-20')}
                    style={{ backgroundColor: o.color ?? '#888', boxShadow: on && o.color ? `0 0 4px 1px ${o.color}55` : undefined }}
                  />
                )
              })}
            </span>
            <span className="text-xs truncate max-w-[120px]">{selected?.name ?? 'Select'}</span>
            <svg className="h-3 w-3 shrink-0 opacity-50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
          collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground px-1.5 mb-1.5">
            Workspace
          </p>
          <div className="flex flex-col gap-1">
            {orgs.map((o) => {
              const active = o.id === selectedId
              const color = o.color ?? '#888'
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => onSelect(o.id)}
                  aria-pressed={active}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium transition-all duration-100 border focus-visible:outline-none',
                    active ? 'opacity-100' : 'opacity-45 hover:opacity-70',
                  )}
                  style={
                    active
                      ? { borderColor: color, color, backgroundColor: `${color}14` }
                      : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }
                  }
                >
                  <span className="block h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color, opacity: active ? 1 : 0.5 }} />
                  <span className="flex-1 truncate text-left">{o.name}</span>
                  <span
                    className={cn('shrink-0 rounded px-1 py-px text-[10px] font-semibold leading-none border', active ? 'opacity-80' : 'opacity-40')}
                    style={active ? { borderColor: color, color } : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}
                  >
                    {abbreviate(o.name)}
                  </span>
                </button>
              )
            })}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export function GitSourcesSettings() {
  const { activeWorkspaceIds } = useWorkspace()
  const [adminOrgs, setAdminOrgs] = useState<AdminOrg[]>([])
  const [selectedOrgId, setSelectedOrgId] = useState<string>('')
  const [sources, setSources] = useState<GitSourceRow[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [showPat, setShowPat] = useState(false)
  const [notAdmin, setNotAdmin] = useState(false)
  /** After POST, poll until this row leaves status cloning (success toast vs error). */
  const [pollUntilId, setPollUntilId] = useState<string | null>(null)
  const [removeId, setRemoveId] = useState<string | null>(null)

  const [repoUrl, setRepoUrl] = useState('')
  const [fnPathPrefix, setFnPathPrefix] = useState('')
  const [branch, setBranch] = useState('main')
  const [subpath, setSubpath] = useState('')
  const [workingBranchPattern, setWorkingBranchPattern] = useState('flownotes/{slug}')
  const [pat, setPat] = useState('')
  const [provider, setProvider] = useState<'auto' | 'github' | 'gitlab' | 'other'>('auto')

  const loadSources = useCallback(async () => {
    if (!selectedOrgId) return
    try {
      const res = await fetchWithAuth(`/api/git-sources?organizationId=${selectedOrgId}`)
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        throw new Error(data.error || res.statusText)
      }
      if (data.success && Array.isArray(data.gitSources)) {
        setSources(data.gitSources)
      }
    } catch {
      toast.error('Failed to load Git sources')
    } finally {
      setLoading(false)
    }
  }, [selectedOrgId])

  // Load user's orgs where they are admin/owner; default to the currently active workspace
  useEffect(() => {
    fetchWithAuth('/api/organizations')
      .then((r) => r.json())
      .then((data) => {
        const orgs: AdminOrg[] = (Array.isArray(data) ? data : (data.organizations ?? []))
          .filter((o: AdminOrg) => o.role === 'admin' || o.role === 'owner')
        setAdminOrgs(orgs)
        if (orgs.length > 0) {
          // Prefer the first org that matches the current sidebar active scope
          const activeMatch = orgs.find((o) => activeWorkspaceIds.includes(o.id))
          setSelectedOrgId((activeMatch ?? orgs[0]).id)
        } else {
          setLoading(false)
        }
      })
      .catch(() => setLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // only on mount — user can override via the picker after that

  useEffect(() => {
    if (selectedOrgId) {
      setLoading(true)
      setSources([])
      loadSources()
    }
  }, [selectedOrgId, loadSources])

  const needsPoll = useMemo(() => sources.some((s) => s.status === 'cloning'), [sources])

  useEffect(() => {
    if (!needsPoll) return
    const id = window.setInterval(() => {
      loadSources()
    }, 3000)
    return () => window.clearInterval(id)
  }, [needsPoll, loadSources])

  useEffect(() => {
    if (!pollUntilId) return
    const row = sources.find((s) => s.id === pollUntilId)
    if (!row) return
    if (row.status === 'active') {
      toast.success('Connected. Documents will appear in your file tree shortly.')
      setPollUntilId(null)
    } else if (row.status === 'error') {
      toast.error('Git clone failed. Check repository URL, branch, and credentials.')
      setPollUntilId(null)
    }
  }, [sources, pollUntilId])

  const sortedSources = useMemo(() => {
    return [...sources].sort((a, b) => {
      const p = (s: GitSourceRow) => (s.status === 'inactive' ? 1 : 0)
      const d = p(a) - p(b)
      if (d !== 0) return d
      return a.fnPathPrefix.localeCompare(b.fnPathPrefix)
    })
  }, [sources])

  const resetForm = () => {
    setRepoUrl('')
    setFnPathPrefix('')
    setBranch('main')
    setSubpath('')
    setWorkingBranchPattern('flownotes/{slug}')
    setPat('')
    setProvider('auto')
    setShowPat(false)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      const body: Record<string, unknown> = {
        organizationId: selectedOrgId,
        repoUrl,
        branch,
        fnPathPrefix,
        subpath,
        workingBranchPattern,
      }
      if (pat.trim()) {
        body.pat = pat.trim()
        if (provider !== 'auto') body.provider = provider
      }
      const res = await fetchWithAuth('/api/git-sources', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const data = await res.json().catch(() => ({}))
      if (res.status === 403) {
        setNotAdmin(true)
        throw new Error('Admin or owner role required to connect Git repositories.')
      }
      if (!res.ok) {
        throw new Error(data.error || res.statusText)
      }
      if (data.gitSourceId) setPollUntilId(data.gitSourceId)
      toast.message('Cloning repository...')
      setShowAdd(false)
      resetForm()
      await loadSources()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to connect repository')
    } finally {
      setSubmitting(false)
    }
  }

  const confirmRemove = async () => {
    if (!removeId) return
    try {
      const res = await fetchWithAuth(`/api/git-sources/${removeId}?organizationId=${selectedOrgId}`, { method: 'DELETE' })
      const data = await res.json().catch(() => ({}))
      if (res.status === 403) {
        setNotAdmin(true)
        throw new Error('Admin or owner role required.')
      }
      if (!res.ok) throw new Error(data.error || res.statusText)
      toast.success('Git source removed')
      setRemoveId(null)
      await loadSources()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove')
    }
  }

  if (loading && adminOrgs.length === 0) {
    return (
      <div className="flex justify-center py-12" data-testid={testIds.gitSourcesSettings.tab}>
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (adminOrgs.length === 0) {
    return (
      <div className="space-y-2 py-8" data-testid={testIds.gitSourcesSettings.tab}>
        <p className="text-sm text-muted-foreground text-center">
          You need admin or owner role in a workspace to manage Git sources.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6" data-testid={testIds.gitSourcesSettings.tab}>
      {adminOrgs.length > 1 && (
        <OrgRadioSelector orgs={adminOrgs} selectedId={selectedOrgId} onSelect={(id) => { setSelectedOrgId(id); setShowAdd(false) }} />
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            Git Sources
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            External repositories appear under your chosen path prefix in the sidebar.
          </p>
        </div>
        {!showAdd && (
          <Button
            size="sm"
            onClick={() => setShowAdd(true)}
            data-testid={testIds.gitSourcesSettings.addButton}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Git Source
          </Button>
        )}
      </div>

      {notAdmin && (
        <p className="text-xs text-muted-foreground">
          Admin or owner role required to connect or remove Git sources.
        </p>
      )}

      {sortedSources.filter((s) => s.status !== 'inactive').length === 0 && !showAdd && (
        <p className="text-sm text-muted-foreground border border-dashed rounded-lg p-6 text-center">
          No external Git repositories connected yet.
        </p>
      )}

      <div className="space-y-4" data-testid={testIds.gitSourcesSettings.list}>
        {loading && (
          <div className="flex justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        {!loading && sortedSources.map((row) => (
          <div
            key={row.id}
            className="rounded-lg border bg-card p-4 space-y-2"
            data-testid={testIds.gitSourcesSettings.row(row.id)}
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 space-y-1">
                <p className="font-medium truncate">{row.fnPathPrefix || '(root)'}</p>
                <p className="text-xs text-muted-foreground truncate font-mono">{row.repoUrl}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className={`border ${statusBadgeClass[row.status] ?? statusBadgeClass.paused}`}>
                  {row.status}
                </Badge>
                {row.status !== 'inactive' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-destructive"
                    aria-label="Remove Git source"
                    onClick={() => setRemoveId(row.id)}
                    data-testid={testIds.gitSourcesSettings.removeButton(row.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>
                branch: <span className="text-foreground">{row.branch}</span>
              </span>
              <span>
                auth:{' '}
                <span className="text-foreground">
                  {row.usesStoredCredentials ? 'HTTPS token (stored)' : 'SSH / system git'}
                </span>
              </span>
              <span>
                last synced:{' '}
                <span className="text-foreground">
                  {row.status === 'cloning'
                    ? 'Cloning...'
                    : row.lastPulledAt
                      ? formatDistanceToNow(row.lastPulledAt)
                      : '-'}
                </span>
              </span>
            </div>
          </div>
        ))}
      </div>

      {showAdd && (
        <form
          onSubmit={handleSubmit}
          className="rounded-lg border bg-muted/40 p-4 space-y-4"
          data-testid={testIds.gitSourcesSettings.addForm}
        >
          <div className="space-y-2">
            <Label htmlFor="gs-repo-url">Repository URL *</Label>
            <Input
              id="gs-repo-url"
              placeholder="https://github.com/org/repo.git"
              value={repoUrl}
              onChange={(e) => setRepoUrl(e.target.value)}
              required
              data-testid={testIds.gitSourcesSettings.repoUrlInput}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gs-prefix">Local path prefix *</Label>
            <Input
              id="gs-prefix"
              placeholder="gschool/"
              value={fnPathPrefix}
              onChange={(e) => setFnPathPrefix(e.target.value)}
              required
              data-testid={testIds.gitSourcesSettings.fnPathPrefixInput}
            />
            <p className="text-xs text-muted-foreground">
              Documents from this repo appear under this path in Maater.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="gs-branch">Branch</Label>
              <Input
                id="gs-branch"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                data-testid={testIds.gitSourcesSettings.branchInput}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gs-subpath">Subpath (optional)</Label>
              <Input
                id="gs-subpath"
                placeholder="docs/"
                value={subpath}
                onChange={(e) => setSubpath(e.target.value)}
                data-testid={testIds.gitSourcesSettings.subpathInput}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="gs-working-branch">Working branch pattern</Label>
            <Input
              id="gs-working-branch"
              value={workingBranchPattern}
              onChange={(e) => setWorkingBranchPattern(e.target.value)}
              data-testid={testIds.gitSourcesSettings.workingBranchPatternInput}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gs-provider">Host (when using token)</Label>
            <select
              id="gs-provider"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              value={provider}
              onChange={(e) => setProvider(e.target.value as typeof provider)}
              data-testid={testIds.gitSourcesSettings.providerSelect}
            >
              <option value="auto">Auto-detect from URL</option>
              <option value="github">GitHub</option>
              <option value="gitlab">GitLab</option>
              <option value="other">Other (OAuth2-style URL)</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="gs-pat">Personal Access Token</Label>
            <div className="relative">
              <Input
                id="gs-pat"
                type={showPat ? 'text' : 'password'}
                placeholder=""
                autoComplete="off"
                value={pat}
                onChange={(e) => setPat(e.target.value)}
                className="pr-10"
                data-testid={testIds.gitSourcesSettings.patInput}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 shrink-0"
                onClick={() => setShowPat(!showPat)}
                aria-label={showPat ? 'Hide token' : 'Show token'}
                data-testid={testIds.gitSourcesSettings.patToggle}
              >
                {showPat ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground space-y-1">
              <span className="block">
                GitHub: Settings &gt; Developer settings &gt; Personal access tokens (repo read + write).
              </span>
              <span className="block">
                GitLab: Settings &gt; Access Tokens (read_repository + write_repository).
              </span>
              <span className="block text-yellow-600 dark:text-yellow-500">
                Leave blank only for local dev where SSH is configured. Cloud deployments require a PAT.
              </span>
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAdd(false)
                resetForm()
              }}
              disabled={submitting}
              data-testid={testIds.gitSourcesSettings.cancelButton}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} data-testid={testIds.gitSourcesSettings.submitButton}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Connecting...
                </>
              ) : (
                'Connect repository'
              )}
            </Button>
          </div>
        </form>
      )}

      <AlertDialog open={Boolean(removeId)} onOpenChange={(open) => !open && setRemoveId(null)}>
        <AlertDialogContent data-testid={testIds.gitSourcesSettings.removeConfirm}>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Git source?</AlertDialogTitle>
            <AlertDialogDescription>
              This disconnects the repository from Maater. Documents already synced may remain until cleaned up separately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmRemove()}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
